/**
 * 의약품 목록 화면.
 * admin-ruda-web `drugInfo.js` 포팅 (목록·검색·수기 등록).
 * 검색 조건은 URL search params로 유지해 상세 복귀(뒤로가기) 시 재조회된다.
 * 엑셀은 현재 검색 조건 그대로 서버가 만들어 서명 URL을 준다.
 * 기준년월 기본값은 공개된 최신 월 — 미공개 월은 서버가 404로 막는다.
 */
import { isDrugExportEmptyError } from '@/entities/drug/api/drug.api';
import {
  useDrugList,
  useExportDrugs,
  useLatestReleasedDrugRefYm,
} from '@/entities/drug/hooks/useDrugs';
import { DrugBulkUploadDialog } from '@/pages/drugs/DrugBulkUploadDialog';
import { DrugInfoCreateFromSettlementModal } from '@/pages/drugs/DrugInfoCreateFromSettlementModal';
import {
  formatPrice,
  REIMBURSEMENT_OPTIONS,
} from '@/pages/drugs/drugListUtils';
import { DataCard } from '@/shared/components/DataCard';
import { MonthPicker } from '@/shared/components/DatePicker';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/shared/components/ui/radio-group';
import { FilterRow, Stack } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { drugDetailPath, ROUTE_PATHS } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { openPresignedFile } from '@/shared/utils/openPresignedFile';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate } from '@tanstack/react-router';
import { Database, Download, Pill, Plus, Upload } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const DRUG_LIST_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  // 비워 두면 공개된 최신 월을 쓴다 (URL에 남기지 않아 공개 후 재진입 시 새 월로 바뀜)
  refYm: '',
  drugCode: '',
  drugName: '',
  pharmaCompanyName: '',
  reimbursementType: '',
  underContract: false,
  manualOnly: false,
};

export const DrugsPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(DRUG_LIST_SEARCH_DEFAULTS);

  const [drugCodeInput, setDrugCodeInput] = useState(values.drugCode);
  const [drugNameInput, setDrugNameInput] = useState(values.drugName);
  const [pharmaNameInput, setPharmaNameInput] = useState(
    values.pharmaCompanyName,
  );
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const exportMutation = useExportDrugs();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const isExporting = exportMutation.isPending;

  useEffect(() => {
    setDrugCodeInput(values.drugCode);
    setDrugNameInput(values.drugName);
    setPharmaNameInput(values.pharmaCompanyName);
  }, [values.drugCode, values.drugName, values.pharmaCompanyName]);

  useEffect(() => {
    setGlobalLoading(isExporting, '의약품 목록 엑셀을 준비하는 중...');
    return () => setGlobalLoading(false);
  }, [isExporting, setGlobalLoading]);

  const latestRefYmQuery = useLatestReleasedDrugRefYm();
  const refYm = values.refYm || latestRefYmQuery.data || '';
  // 사용자가 월을 고르지 않았다면 최신 공개 월 응답 전엔 목록을 부르지 않는다.
  // 최신 월 조회가 실패하면 refYm 없이 보내 서버 기본값(같은 최신 공개 월)에 맡긴다.
  const isWaitingLatestRefYm = !values.refYm && latestRefYmQuery.isPending;

  const search = useMemo(
    () => ({
      refYm: refYm || undefined,
      drugCode: values.drugCode || undefined,
      drugName: values.drugName || undefined,
      pharmaCompanyName: values.pharmaCompanyName || undefined,
      reimbursementType: values.reimbursementType || undefined,
      contractStatus: values.underContract ? true : undefined,
      dataSourceType: values.manualOnly ? 'ADMIN' : undefined,
    }),
    [values, refYm],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error, refetch } = useDrugList({
    search,
    pageable,
    enabled: !isWaitingLatestRefYm,
  });

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const drugs = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      drugCode: drugCodeInput.trim(),
      drugName: drugNameInput.trim(),
      pharmaCompanyName: pharmaNameInput.trim(),
      page: 1,
    });
  };

  const handleExport = async () => {
    try {
      const archive = await exportMutation.mutateAsync(search);
      if (!archive.downloadUrl) {
        toast.info('다운로드할 의약품이 없습니다.');
        return;
      }
      openPresignedFile(archive.downloadUrl);
      toast.success(
        archive.refYm
          ? `의약품 목록을 다운로드했습니다. (${archive.refYm}, ${archive.rowCount.toLocaleString()}건)`
          : `의약품 목록을 다운로드했습니다. (${archive.rowCount.toLocaleString()}건)`,
      );
    } catch (e) {
      if (isDrugExportEmptyError(e)) {
        toast.info('다운로드할 의약품이 없습니다.');
        return;
      }
      handleApiError(e, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Pill}
        title="의약품 관리"
        description="의약품 목록 조회 및 수기 등록"
        actions={
          <>
            <MenuWriteGate>
              <Button
                variant="outline"
                onClick={() =>
                  navigate({
                    to: ROUTE_PATHS.DRUG_DATA_CREATE,
                    search: { refYm },
                  } as never)
                }
              >
                <Database />
                의약품 정보 생성
              </Button>
            </MenuWriteGate>
            <MenuWriteGate>
              <Button onClick={() => setCreateModalOpen(true)}>
                <Plus />
                의약품 등록
              </Button>
            </MenuWriteGate>
            <MenuWriteGate>
              <Button variant="outline" onClick={() => setBulkUploadOpen(true)}>
                <Upload />
                대량 업로드
              </Button>
            </MenuWriteGate>
            <Button
              variant="outline"
              onClick={() => void handleExport()}
              disabled={isExporting || isLoading || totalCount === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? '처리 중...' : '엑셀 다운로드'}
            </Button>
          </>
        }
      />

      <FilterCard>
        <Stack as="form" gap={16} onSubmit={handleSearch}>
          <FilterRow as="div" gap={12}>
            <Field label="기준년월" htmlFor="ref-ym">
              <MonthPicker
                id="ref-ym"
                value={refYm}
                onChange={(next) => setValues({ refYm: next, page: 1 })}
                className="w-44"
              />
            </Field>
          </FilterRow>

          <FilterRow as="div" gap={12}>
            <Field label="의약품 코드" htmlFor="drug-code">
              <Input
                id="drug-code"
                placeholder="의약품 코드"
                value={drugCodeInput}
                onChange={(e) => setDrugCodeInput(e.target.value)}
                className="w-40"
              />
            </Field>
            <Field label="의약품명" htmlFor="drug-name">
              <Input
                id="drug-name"
                placeholder="의약품명"
                value={drugNameInput}
                onChange={(e) => setDrugNameInput(e.target.value)}
                className="w-40"
              />
            </Field>
            <Field label="제약사명" htmlFor="pharma-name">
              <Input
                id="pharma-name"
                placeholder="제약사명"
                value={pharmaNameInput}
                onChange={(e) => setPharmaNameInput(e.target.value)}
                className="w-40"
              />
            </Field>
            <Button type="submit" className="ruda-button">검색</Button>
          </FilterRow>

          <div className="space-y-2">
            <Label>급여 구분</Label>
            <RadioGroup
              value={values.reimbursementType || 'all'}
              onValueChange={(value) =>
                setValues({
                  reimbursementType: value === 'all' ? '' : value,
                  page: 1,
                })
              }
              className="flex flex-wrap gap-4"
            >
              {REIMBURSEMENT_OPTIONS.map((opt) => {
                const value = opt.value || 'all';
                return (
                  <label
                    key={value}
                    htmlFor={`reimbursement-${value}`}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <RadioGroupItem
                      value={value}
                      id={`reimbursement-${value}`}
                    />
                    {opt.label}
                  </label>
                );
              })}
            </RadioGroup>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={values.underContract}
                onCheckedChange={(v) =>
                  setValues({ underContract: v === true, page: 1 })
                }
              />
              계약 중
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={values.manualOnly}
                onCheckedChange={(v) =>
                  setValues({ manualOnly: v === true, page: 1 })
                }
              />
              수기 등록 의약품만 보기
            </label>
          </div>
        </Stack>
      </FilterCard>

      <DataCard>
        {isLoading || isWaitingLatestRefYm ? (
          <div className="py-16">
            <LoadingSpinner tip="의약품 목록을 불러오는 중..." />
          </div>
        ) : drugs.length === 0 ? (
          <EmptyState description="검색 조건에 맞는 의약품이 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>의약품 코드</TableHead>
                  <TableHead>품목 기준 코드</TableHead>
                  <TableHead>제약사 약품 코드</TableHead>
                  <TableHead>제약사명</TableHead>
                  <TableHead>의약품명</TableHead>
                  <TableHead>주성분코드</TableHead>
                  <TableHead>약가 심평원</TableHead>
                  <TableHead>급여/비급여 구분</TableHead>
                  <TableHead>정보 구분</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drugs.map((drug) => (
                  <TableRow
                    key={`${drug.refYm}-${drug.drugCode}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate({
                        to: drugDetailPath(drug.drugCode),
                        search: { refYm },
                      } as never)
                    }
                  >
                    <TableCell className="font-medium">{drug.drugCode}</TableCell>
                    <TableCell>{drug.drugCodeMfds || '-'}</TableCell>
                    <TableCell>{drug.drugCodePharma || '-'}</TableCell>
                    <TableCell>{drug.pharmaCompanyName || '-'}</TableCell>
                    <TableCell>{drug.drugName}</TableCell>
                    <TableCell>{drug.mainIngredientCode || '-'}</TableCell>
                    <TableCell>{formatPrice(drug.priceHira)}</TableCell>
                    <TableCell>{drug.reimbursementType || '-'}</TableCell>
                    <TableCell>{drug.dataSourceType || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>

      <DrugBulkUploadDialog
        open={bulkUploadOpen}
        onOpenChange={setBulkUploadOpen}
        defaultRefYm={refYm}
        onSuccess={() => void refetch()}
      />
      <DrugInfoCreateFromSettlementModal
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onSuccess={() => void refetch()}
        refYm={refYm}
      />
    </div>
  );
};
